/**
 * Shared landing primitives: kicker, serif headings, CTA link, carousel rail.
 * Everything consumes design tokens (bg-forest / bg-amber / bg-cream / hero-*).
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ChevronLeft, ChevronRight, Star, StarHalf } from 'lucide-react';
import { cn } from '@finmin/ui';

/** Letterspaced amber section kicker, e.g. "HOW FINMINOS WORKS". */
export function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-amber', className)}>
      {children}
    </p>
  );
}

/**
 * Two-tone landing heading: a solid serif line + an italic amber line,
 * matching the Figma's recurring "Heading. / accent line" pattern.
 */
export function SerifHeading({
  line1,
  line2,
  dark = false,
  className,
}: {
  line1: React.ReactNode;
  /** Italic amber accent line (optional). */
  line2?: React.ReactNode;
  /** True on forest sections (line1 renders cream instead of forest). */
  dark?: boolean;
  className?: string;
}) {
  return (
    <h2
      className={cn(
        'font-landing font-bold leading-[1.08] text-3xl sm:text-4xl lg:text-5xl',
        dark ? 'text-cream' : 'text-forest',
        className,
      )}
    >
      {line1}
      {line2 != null && (
        <>
          <br />
          <em className="italic text-amber font-bold">{line2}</em>
        </>
      )}
    </h2>
  );
}

/** Amber pill CTA linking into the app (login). */
export function CtaLink({
  children = 'File Your ITR Now',
  className,
  size = 'md',
}: {
  children?: React.ReactNode;
  className?: string;
  size?: 'md' | 'lg';
}) {
  return (
    <Link
      to="/login"
      className={cn(
        'inline-flex items-center gap-2 rounded-xl bg-amber font-semibold text-white',
        'shadow-[0_6px_20px_-6px_hsl(var(--palette-amber)/0.65)] transition-all',
        'hover:brightness-95 hover:shadow-[0_8px_24px_-6px_hsl(var(--palette-amber)/0.8)] active:scale-95',
        size === 'lg' ? 'px-7 py-3.5 text-base' : 'px-5 py-2.5 text-sm',
        className,
      )}
    >
      {children}
      <ArrowRight className="h-4 w-4" aria-hidden />
    </Link>
  );
}

/** Amber rounded-square arrow button for carousels (Figma: ~40px, 8px radius). */
function RailArrow({ dir, onClick }: { dir: 'left' | 'right'; onClick: () => void }) {
  const Icon = dir === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'left' ? 'Scroll left' : 'Scroll right'}
      className="h-10 w-10 rounded-[8px] bg-amber text-white flex items-center justify-center transition-all hover:brightness-95 active:scale-95"
    >
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  );
}

/**
 * Horizontal snap rail with the design's centered amber arrows underneath.
 * Children are the cards; each should set its own fixed width.
 */
export function Rail({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = React.useRef<HTMLDivElement>(null);

  function scrollByCard(dir: 1 | -1) {
    const el = ref.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  return (
    <div className={className}>
      <div ref={ref} className="landing-rail flex gap-4 overflow-x-auto pb-2">
        {children}
      </div>
      <div className="mt-5 flex items-center justify-center gap-2">
        <RailArrow dir="left" onClick={() => scrollByCard(-1)} />
        <RailArrow dir="right" onClick={() => scrollByCard(1)} />
      </div>
    </div>
  );
}

/**
 * Infinite auto-scrolling card marquee — the Figma keeps a "Duplicate set for
 * infinite" layer for these rails. Full-bleed across the viewport, pauses on
 * hover, respects prefers-reduced-motion (see landing.css). The duplicate set
 * is aria-hidden. Children must set their own fixed width + shrink-0.
 */
export function CardMarquee({
  children,
  className,
  /** Times the card set is repeated per loop half — bump when one set is
   *  narrower than the viewport (a half must be ≥ 100vw for a seamless loop). */
  repeat = 1,
}: {
  children: React.ReactNode;
  className?: string;
  repeat?: number;
}) {
  const half = (hidden: boolean) => (
    <div className="flex shrink-0 items-stretch gap-5 pr-5" aria-hidden={hidden || undefined}>
      {Array.from({ length: repeat }).map((_, i) => (
        // Cards inside repeat>1 halves get remounted per copy; the fragment key
        // scopes their own keys so React doesn't see duplicates.
        <React.Fragment key={i}>{children}</React.Fragment>
      ))}
    </div>
  );
  return (
    <div className={cn('relative left-1/2 w-screen -translate-x-1/2 overflow-hidden', className)}>
      <div className="landing-marquee landing-marquee--cards">
        {half(false)}
        {half(true)}
      </div>
    </div>
  );
}

/** Amber star row; supports half stars (e.g. 4.5). */
export function Stars({ count }: { count: number }) {
  const full = Math.floor(count);
  const half = count - full >= 0.5;
  return (
    <span className="flex items-center gap-0.5 text-amber" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: full }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-current" aria-hidden />
      ))}
      {half && <StarHalf className="h-3.5 w-3.5 fill-current" aria-hidden />}
    </span>
  );
}

/**
 * Grayscale text-logo marquee row. Figma: DM Sans Bold 19px, #8FA8A5 @55%,
 * with soft gradient fades at both edges.
 */
export function LogoMarquee({
  logos,
  dark = false,
  /** Section bg the edge fades blend into ('white' | 'cream' etc. tailwind color name). */
  fade = 'white',
}: {
  logos: string[];
  dark?: boolean;
  fade?: 'white' | 'cream' | 'parchment';
}) {
  // Duplicate the list so the -50% translate loops seamlessly.
  const doubled = [...logos, ...logos];
  const fadeFrom =
    fade === 'cream' ? 'from-cream' : fade === 'parchment' ? 'from-parchment' : 'from-white';
  return (
    <div className="relative overflow-hidden" aria-hidden>
      <div className="landing-marquee items-center gap-14 pr-14">
        {doubled.map((logo, i) => (
          <span
            key={`${logo}-${i}`}
            className={cn(
              'whitespace-nowrap font-landing-body text-[19px] font-bold tracking-wide',
              dark ? 'text-cream/40' : 'text-mist/55',
            )}
          >
            {logo}
          </span>
        ))}
      </div>
      {/* Edge fades (the design's Gradient masks) */}
      <div className={cn('pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r to-transparent', fadeFrom)} />
      <div className={cn('pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l to-transparent', fadeFrom)} />
    </div>
  );
}
